import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Zap, Plus, Search, Edit2, Trash2, Loader2, X, Save } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  collection, query, orderBy, getDocs,
  doc, updateDoc, deleteDoc, addDoc, serverTimestamp, increment,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
 
interface SMSTemplate {
  id: string;
  title: string;
  body: string;
  category: string;
  language: string;
  usage_count: number;
  created_by: string;
  is_shared: boolean;
}
 
const CATEGORIES = [
  { value: 'general', label: 'Général' }, { value: 'greeting', label: 'Salutation' },
  { value: 'follow_up', label: 'Suivi' }, { value: 'marketing', label: 'Marketing' },
  { value: 'appointment', label: 'Rendez-vous' }, { value: 'closing', label: 'Clôture' },
];
 
function normalizeTemplate(id: string, data: any): SMSTemplate {
  return {
    id, title: data.title || '', body: data.body || '',
    category: data.category || 'general', language: data.language || 'fr',
    usage_count: data.usageCount || data.usage_count || 0,
    created_by: data.createdBy || data.created_by || '',
    is_shared: data.isShared ?? data.is_shared ?? false,
  };
}
 
interface SMSSnippetsPanelProps {
  onSelectSnippet: (body: string) => void;
  isOpen: boolean;
  onClose: () => void;
}
 
export function SMSSnippetsPanel({ onSelectSnippet, isOpen, onClose }: SMSSnippetsPanelProps) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<SMSTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SMSTemplate | null>(null);
 
  const loadTemplates = useCallback(async () => {
    try {
      const snap = await getDocs(
        query(collection(db, 'smsTemplates'), orderBy('usageCount', 'desc'))
      );
      setTemplates(snap.docs.map(d => normalizeTemplate(d.id, d.data())));
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);
 
  useEffect(() => { if (isOpen) loadTemplates(); }, [isOpen, loadTemplates]);
 
  const handleUseSnippet = async (template: SMSTemplate) => {
    onSelectSnippet(template.body);
    try {
      await updateDoc(doc(db, 'smsTemplates', template.id), { usageCount: increment(1) });
      setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, usage_count: t.usage_count + 1 } : t));
    } catch {}
    onClose();
    toast.success('Snippet inséré');
  };
 
  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'smsTemplates', id));
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template supprimé');
    } catch {
      toast.error('Erreur');
    }
  };
 
  const filtered = templates.filter(t => {
    if (filterCategory !== 'all' && t.category !== filterCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return t.title.toLowerCase().includes(q) || t.body.toLowerCase().includes(q);
    }
    return true;
  });
 
  if (!isOpen) return null;
 
  return (
    <div className="absolute bottom-16 left-0 right-0 mx-3 bg-[hsl(220,20%,12%)] border border-white/10 rounded-xl shadow-2xl z-50 max-h-80 flex flex-col">
      <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-semibold text-white">Réponses rapides</span>
          <Badge variant="secondary" className="text-[10px] h-4 bg-white/10 text-white/50">{templates.length}</Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6 text-white/40 hover:text-white" onClick={() => { setEditingTemplate(null); setShowCreateDialog(true); }}>
            <Plus className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-white/40 hover:text-white" onClick={onClose}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
 
      <div className="px-3 py-2 flex items-center gap-2 border-b border-white/5">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/30" />
          <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-7 h-7 text-[11px] bg-white/5 border-white/10 text-white placeholder:text-white/30" />
        </div>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="h-7 text-[11px] bg-white/5 border border-white/10 text-white rounded-md px-2">
          <option value="all">Toutes</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
 
      <ScrollArea className="flex-1 max-h-48">
        {loading ? (
          <div className="flex items-center justify-center p-6"><Loader2 className="w-4 h-4 animate-spin text-white/30" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-6 text-white/30">
            <Zap className="w-6 h-6 mb-1.5 opacity-50" />
            <p className="text-[11px]">Aucun snippet trouvé</p>
            <Button variant="ghost" size="sm" className="mt-2 h-6 text-[11px] text-amber-400 hover:text-amber-300"
              onClick={() => { setEditingTemplate(null); setShowCreateDialog(true); }}>
              <Plus className="w-3 h-3 mr-1" /> Créer un snippet
            </Button>
          </div>
        ) : (
          filtered.map(t => (
            <button key={t.id} onClick={() => handleUseSnippet(t)}
              className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-white">{t.title}</span>
                  <Badge variant="outline" className="text-[9px] h-4 border-white/10 text-white/40">
                    {CATEGORIES.find(c => c.value === t.category)?.label || t.category}
                  </Badge>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={e => { e.stopPropagation(); setEditingTemplate(t); setShowCreateDialog(true); }} className="p-1 text-white/30 hover:text-white">
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); handleDelete(t.id); }} className="p-1 text-white/30 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-white/40 mt-0.5 line-clamp-2">{t.body}</p>
              <span className="text-[9px] text-white/20 mt-0.5 block">Utilisé {t.usage_count}x</span>
            </button>
          ))
        )}
      </ScrollArea>
 
      <SnippetFormDialog isOpen={showCreateDialog} onClose={() => { setShowCreateDialog(false); setEditingTemplate(null); }}
        template={editingTemplate} userId={user?.id || ''}
        onSaved={() => { loadTemplates(); setShowCreateDialog(false); setEditingTemplate(null); }} />
    </div>
  );
}
 
function SnippetFormDialog({ isOpen, onClose, template, userId, onSaved }: {
  isOpen: boolean; onClose: () => void; template: SMSTemplate | null; userId: string; onSaved: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('general');
  const [saving, setSaving] = useState(false);
 
  useEffect(() => {
    if (template) { setTitle(template.title); setBody(template.body); setCategory(template.category); }
    else { setTitle(''); setBody(''); setCategory('general'); }
  }, [template, isOpen]);
 
  const handleSave = async () => {
    if (!title.trim() || !body.trim()) { toast.error('Titre et contenu requis'); return; }
    setSaving(true);
    try {
      if (template) {
        await updateDoc(doc(db, 'smsTemplates', template.id), {
          title: title.trim(), body: body.trim(), category, updatedAt: serverTimestamp(),
        });
        toast.success('Template mis à jour');
      } else {
        await addDoc(collection(db, 'smsTemplates'), {
          title: title.trim(), body: body.trim(), category,
          createdBy: userId, usageCount: 0, isShared: false,
          language: 'fr', createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
        toast.success('Template créé');
      }
      onSaved();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
 
  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="bg-[hsl(220,20%,12%)] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle className="text-sm">{template ? 'Modifier le snippet' : 'Nouveau snippet'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Titre du snippet" value={title} onChange={e => setTitle(e.target.value)}
            className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 text-sm bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[hsl(220,20%,15%)] border-white/10 text-white">
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea placeholder="Contenu du message..." value={body} onChange={e => setBody(e.target.value)}
            className="min-h-[100px] text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30" />
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white/50 hover:text-white">Annuler</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            {template ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
 